import type { ContentHref } from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import { selectApiWithPendingChanges } from '@newStore/documentApi/documentApiSelectors';
import {
  selectAppliedNodeConfig,
  selectEditConfigForNode,
  selectWebTabForNode,
} from '@newStore/documentUI/nodeTypeConfigSelectors';
import { createTypedSelector } from '@newStore/genericHelpers';
import type { FilterableEditComponent } from '@nodeTypeConfig/configTypes';
import { conditionalLogTime } from '@store/helpers/generalUtils';
import { type AsyncValidationRule, isAsyncValidationRule } from './validationTypes';

/**
 * TODO: we could optimize here, because we don't even need the content, we just want all the hrefs to iterate over.
 */

const selectAsyncValidationsPerContentNode = (
  state: RootState,
): Record<ContentHref, AsyncValidationRule[]> => {
  const { content } = selectApiWithPendingChanges(state);

  const validations = Object.keys(content).reduce((acc, hrefString) => {
    const href = hrefString as ContentHref;

    const editConfigList: FilterableEditComponent[] = selectEditConfigForNode(state, href);
    const webTabList = selectWebTabForNode(state, href);
    const mainConfigList = selectAppliedNodeConfig(state, href);

    const asyncValidationRules: AsyncValidationRule[] = [];

    editConfigList.forEach((edit) => {
      if ('validations' in edit) {
        const editValidations: AsyncValidationRule[] = edit.validations.filter((z) =>
          isAsyncValidationRule(z),
        );

        asyncValidationRules.push(...editValidations);
      }
    });

    webTabList?.forEach((edit) => {
      if ('validations' in edit) {
        const editValidations: AsyncValidationRule[] = edit.validations.filter((z) =>
          isAsyncValidationRule(z),
        );

        asyncValidationRules.push(...editValidations);
      }
    });

    if ('validationRules' in mainConfigList) {
      const editValidations: AsyncValidationRule[] = mainConfigList.validationRules.filter((z) =>
        isAsyncValidationRule(z)
      );

      asyncValidationRules.push(...editValidations);
    }

    if (asyncValidationRules.length) {
      acc[href] = asyncValidationRules;
    }

    return acc;
  }, {});

  return validations;
};

export const selectIsValidationFinishedLoading = (state: RootState) => {
  return Object.values(state.validationData).every((node) =>
    Object.values(node).every((validation) => validation.isLoading === false),
  );
};

export const selectFlatValidationRules = createTypedSelector(
  [(state) => selectAsyncValidationsPerContentNode(state)],
  (validationsPerNode) => {
    const validations = [] as Array<{
      href: ContentHref;
      validation: AsyncValidationRule;
    }>;

    for (const [href, validationRules] of Object.entries(validationsPerNode)) {
      for (const validation of validationRules as AsyncValidationRule[]) {
        validations.push({
          href,
          validation,
        } as { href: ContentHref; validation: AsyncValidationRule });
      }
    }
    return validations;
  },
);

export type SelectFlatValidationRulesReturnType = ReturnType<typeof selectFlatValidationRules>;

export const selectValidationRulesThatNeedData = (state: RootState) => {
  const endLog = conditionalLogTime('selectValidationRulesThatNeedData', 5);
  const validationsToDo = selectFlatValidationRules(state);

  const validationsThatNeedData = validationsToDo.filter((validation) =>
    validation.validation.selectNeedsData(state, validation.href) ||
    // if we have a selector to invalidate on change, and that selector returns a different value than the validityHash we have stored, we need to refetch
    (typeof validation.validation.selectInvalidateDataOnChangeOf === 'function' &&
      validation.validation.selectInvalidateDataOnChangeOf(state, validation.href) !==
        state.validationData[validation.href]?.[validation.validation.getName()]?.validityHash)
  );
  endLog();
  return validationsThatNeedData;
};

/* export const selectContentNodeValidationErrors = createTypedSelector(
  [
    (state: RootState, href: string) => selectAsyncValidationsPerContentNode(state)[href],
    (state: RootState) => state,
    (state: RootState, href: string) => href,
  ],
  (validationsForNode: Record<string, AsyncValidationRule[]>, state, href) => {
    if (!validationsForNode) {
      return [];
    }

    const errors: {
      error: ValidationError;
      component: string;
    }[] = [];

    Object.keys(validationsForNode).forEach((componentType) => {
      const rules = validationsForNode[componentType];
      const componentErrors = rules
        .map((rule) => rule.selectValidation(state, href))
        .filter((z): z is ValidationError => z !== true && z !== null)
        .map((z) => ({ error: z, component: componentType }));
      errors.push(...componentErrors);
    });

    return errors;
  },
  {
    memoizeOptions: {
      // this is quite ugly, but i don't see a better way yet with the old reselect.
      // we need the whole state to run the validation, so this runs on any state change.
      // for now we just check if the result is equal to the previous result, and keep a max size of 100, so we can store a few validations per node
      // this is to avoid rerenders, which is also not relevant yet.

      /**
       * if this selector were to only return booleans, then we don't need to create a cached selector for this.
       * the problem is, if something is not valid, we return an object, which is not equal to the previous object.
       */
// resultEqualityCheck: isEqual,
/*     },
  }
); */
